import { useEffect, useState } from "react";
import { adminGetSeo, adminUpdateSeo, fetchSettings, adminUpdateSettings, adminPingIndexNow } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { Search, Save, Loader2, Bot, Zap } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const PAGE_LABELS = {
  "/": "Home", "/kathas": "Kathas", "/mantras": "Mantras", "/stotras": "Stotras",
  "/suktas": "Suktas", "/rituals": "Rituals & Pooja Guides", "/vedic-knowledge": "Vedic Knowledge",
  "/festivals": "Festivals", "/temples": "Temples", "/deities": "Deities",
  "/panchang": "Panchangam", "/panchanga-premium": "Panchangam Premium",
  "/calendar": "Calendar", "/patrika": "Patrika (Journal)", "/japa": "Japa",
  "/about": "About", "/services": "Services", "/ganesh-chaturthi": "Ganesh Chaturthi",
};

const G = [
  ["site_name", "Site name"], ["site_url", "Site URL (canonical, e.g. https://dharmartha.org)"],
  ["title_suffix", "Title suffix (appended to page titles)"], ["default_title", "Default title"],
  ["default_description", "Default meta description", true], ["default_keywords", "Default keywords (comma-separated)", true],
  ["og_image", "Default social share image URL"],
  ["favicon", "Site favicon"],
];

export default function AdminSeo() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [crawl, setCrawl] = useState(true);
  const [crawlBusy, setCrawlBusy] = useState(false);
  const [pingBusy, setPingBusy] = useState(false);

  useEffect(() => { adminGetSeo().then(setCfg).catch(() => toast.error("Failed to load SEO settings")); }, []);
  useEffect(() => { fetchSettings().then((s) => setCrawl(s.crawl_enabled !== false)).catch(() => {}); }, []);

  const toggleCrawl = async () => {
    const next = !crawl;
    setCrawlBusy(true);
    setCrawl(next);
    try { await adminUpdateSettings({ crawl_enabled: next }); toast(next ? "Search indexing enabled" : "Search indexing turned OFF (noindex)"); }
    catch (e) { setCrawl(!next); toast.error("Could not update", { description: e.response?.data?.detail || e.message }); }
    finally { setCrawlBusy(false); }
  };

  const setG = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setPage = (path, k, v) => setCfg((c) => ({ ...c, pages: { ...c.pages, [path]: { ...(c.pages?.[path] || {}), [k]: v } } }));
  const [newPath, setNewPath] = useState("");
  const addPage = () => {
    let p = (newPath || "").trim().toLowerCase();
    if (!p) return;
    if (!p.startsWith("/")) p = "/" + p;
    if (cfg.pages?.[p]) { toast("That page is already listed"); setNewPath(""); return; }
    setCfg((c) => ({ ...c, pages: { ...(c.pages || {}), [p]: { title: "", keywords: "", description: "" } } }));
    setNewPath("");
  };

  const pingIndexNow = async () => {
    setPingBusy(true);
    try {
      const r = await adminPingIndexNow();
      if (r.ok) toast("Submitted to IndexNow", { description: `${r.count} URLs sent to Bing / Yandex (and Google's IndexNow trial).` });
      else toast.error("IndexNow could not verify", { description: `Status ${r.status || r.reason}. This works once the key file is live on your deployed domain — redeploy, then retry.` });
    } catch (e) { toast.error("Ping failed", { description: e.response?.data?.detail || e.message }); }
    finally { setPingBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateSeo(cfg);
      setCfg(updated);
      toast("SEO settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!cfg) return <p className="py-20 text-center text-ink-3"><span className="font-deva text-3xl text-saffron om-pulse">ॐ</span></p>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-seo">
      <div className="mb-2 flex items-center gap-2.5">
        <Search className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">SEO & Keywords</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Control how Dharmartha appears on Google and social media. Set site-wide defaults and per-page titles,
        descriptions and keywords. Individual kathas/mantras/etc. can override these in their own editor.
      </p>

      <section className="mb-6 border border-line bg-surface p-6" data-testid="crawl-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><Bot className="h-5 w-5 text-saffron" /> Search-engine crawling</h2>
            <p className="mt-1 max-w-2xl text-sm text-ink-3">
              When ON, every public page tells Google &amp; other engines to <span className="text-ink-2">index &amp; follow</span>.
              When OFF, the whole site emits <span className="font-mono text-xs">noindex</span>.
              Note: on the preview domain a platform header may still block indexing — indexing applies on your live custom domain (dharmartha.org). If bots are still blocked at the edge, contact your hosting support to disable any “Block AI bots” rule.
            </p>
          </div>
          <button
            data-testid="crawl-toggle"
            onClick={toggleCrawl}
            disabled={crawlBusy}
            role="switch"
            aria-checked={crawl}
            className={`relative mt-1 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${crawl ? "bg-emerald-500" : "bg-ink-3/40"} disabled:opacity-60`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${crawl ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className={`mt-3 text-sm font-medium ${crawl ? "text-emerald-600" : "text-amber-600"}`}>{crawl ? "Indexing is ON — search engines may crawl the site." : "Indexing is OFF — search engines are told not to index."}</p>
      </section>

      <section className="mb-6 border border-line bg-surface p-6" data-testid="indexnow-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><Zap className="h-5 w-5 text-saffron" /> Instant indexing (IndexNow)</h2>
            <p className="mt-1 max-w-2xl text-sm text-ink-3">
              Instantly notify <span className="text-ink-2">Bing, Yandex &amp; Seznam</span> (and Google, which is trialing IndexNow) whenever you publish or edit a page — new pages already ping automatically. This also feeds <span className="text-ink-2">ChatGPT Search</span> (it uses the Bing index). Use the button to resubmit the entire site.
              <br />AI model crawlers (GPTBot, Google-Extended, PerplexityBot, ClaudeBot…) have no submit API — they discover via <span className="font-mono text-xs">robots.txt</span> + <span className="font-mono text-xs">llms.txt</span>, both already open to them.
            </p>
          </div>
          <button onClick={pingIndexNow} disabled={pingBusy} data-testid="indexnow-ping" className="mt-1 inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--saffron)] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60">
            {pingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Resubmit all pages
          </button>
        </div>
      </section>

      <section className="border border-line bg-surface p-6">
        <h2 className="mb-4 font-serif-latin text-xl text-ink">Global defaults</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {G.map(([k, label, area]) => (
            <div key={k} className={area || k === "og_image" || k === "favicon" ? "sm:col-span-2" : ""}>
              {k === "og_image" ? (
                <ImageUploader label="Default social share image (og:image)" testid="seo-og-image" value={cfg[k] || ""} onChange={(v) => setG(k, v)} hint="Shown when the site is shared on WhatsApp, X, Facebook, etc." />
              ) : k === "favicon" ? (
                <ImageUploader label="Site favicon (browser tab icon)" testid="seo-favicon" value={cfg[k] || ""} onChange={(v) => setG(k, v)} hint="A small square icon (PNG or ICO, e.g. 32×32 or 512×512) shown in the browser tab." />
              ) : (
                <>
                  <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
                  {area
                    ? <textarea value={cfg[k] || ""} onChange={(e) => setG(k, e.target.value)} rows={2} className={inp} data-testid={`seo-${k}`} />
                    : <input value={cfg[k] || ""} onChange={(e) => setG(k, e.target.value)} className={inp} data-testid={`seo-${k}`} />}
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 border border-line bg-surface p-6">
        <h2 className="mb-1 font-serif-latin text-xl text-ink">Per-page SEO</h2>
        <p className="mb-4 text-xs text-ink-3">Blank fields fall back to the global defaults.</p>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input value={newPath} onChange={(e) => setNewPath(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPage())}
            placeholder="/add-a-page-path" data-testid="seo-new-path" className={`${inp} max-w-xs`} />
          <button type="button" onClick={addPage} data-testid="seo-add-page" className="border border-saffron/40 px-3 py-2 text-sm text-saffron transition-colors hover:bg-saffron/5">Add page</button>
        </div>
        <div className="space-y-5">
          {Object.keys(cfg.pages || {}).map((path) => {
            const p = cfg.pages[path] || {};
            return (
              <div key={path} className="border border-line p-4" data-testid={`seo-page-${path === "/" ? "home" : path.slice(1)}`}>
                <p className="mb-2.5 text-sm font-medium text-ink">{PAGE_LABELS[path] || path} <span className="text-ink-3">· {path}</span></p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={p.title || ""} onChange={(e) => setPage(path, "title", e.target.value)} placeholder="Page title" className={inp} />
                  <input value={p.keywords || ""} onChange={(e) => setPage(path, "keywords", e.target.value)} placeholder="Keywords (comma-separated)" className={inp} />
                  <textarea value={p.description || ""} onChange={(e) => setPage(path, "description", e.target.value)} placeholder="Meta description" rows={2} className={`${inp} sm:col-span-2`} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="sticky bottom-4 mt-6">
        <button onClick={save} disabled={saving} data-testid="seo-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save SEO settings
        </button>
      </div>
    </div>
  );
}
